import { RefObject, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'es-toolkit';

interface UseScrollRestorationProps {
  scrollRef: RefObject<HTMLElement>;
  initialPosition?: number;
  onScrollChange?: (position: number) => void;
  isActive?: boolean;
  debounceMs?: number;
}

export const useScrollRestoration = ({
  scrollRef,
  initialPosition = 0,
  onScrollChange,
  isActive = true,
  debounceMs = 100,
}: UseScrollRestorationProps) => {
  const scrollPositionRef = useRef(initialPosition);

  const debouncedSaveScroll = useMemo(
    () =>
      debounce((scrollTop: number) => {
        scrollPositionRef.current = scrollTop;
        onScrollChange?.(scrollTop);
      }, debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !isActive) return;

    const handleScroll = () => {
      debouncedSaveScroll(element.scrollTop);
    };

    element.addEventListener('scroll', handleScroll);
    return () => {
      element.removeEventListener('scroll', handleScroll);
      debouncedSaveScroll.cancel?.();
    };
  }, [isActive, debouncedSaveScroll]);

  // 스크롤 위치 복원
  useEffect(() => {
    if (scrollRef.current && initialPosition > 0 && isActive) {
      scrollRef.current.scrollTop = initialPosition;
    }
  }, [initialPosition, isActive]);

  return {
    getScrollPosition: () => scrollPositionRef.current,
  };
};
